import React from 'react';
import { FormFieldDef, FormFieldOption, FormFieldType, FormSection } from '../../types/formSchema';

interface Props {
  sections: FormSection[];
  selectedFieldKey: string | null;
  onSectionsChange: (sections: FormSection[]) => void;
}

const FIELD_TYPES: { value: FormFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio' },
  { value: 'number', label: 'Number' },
  { value: 'checkbox', label: 'Checkbox' },
];

function findField(sections: FormSection[], key: string): { field: FormFieldDef; si: number; fi: number } | null {
  for (let si = 0; si < sections.length; si++) {
    for (let fi = 0; fi < sections[si].fields.length; fi++) {
      if (sections[si].fields[fi].key === key) {
        return { field: sections[si].fields[fi], si, fi };
      }
    }
  }
  return null;
}

export default function FieldDetailPanel({ sections, selectedFieldKey, onSectionsChange }: Props) {
  if (!selectedFieldKey) {
    return (
      <div style={emptyStyle}>
        Select a field to edit its properties
      </div>
    );
  }

  const found = findField(sections, selectedFieldKey);
  if (!found) {
    return <div style={emptyStyle}>Field not found</div>;
  }

  const { field, si, fi } = found;

  const updateField = (patch: Partial<FormFieldDef>) => {
    const next = sections.map((s) => ({ ...s, fields: [...s.fields] }));
    next[si].fields[fi] = { ...next[si].fields[fi], ...patch };
    onSectionsChange(next);
  };

  const allFieldKeys = sections.flatMap((s) => s.fields.map((f) => f.key)).filter((k) => k !== field.key);

  const updateOption = (idx: number, patch: Partial<FormFieldOption>) => {
    const opts = [...(field.options || [])];
    opts[idx] = { ...opts[idx], ...patch };
    updateField({ options: opts });
  };

  const addOption = () => {
    const opts = [...(field.options || []), { value: '', label: '' }];
    updateField({ options: opts });
  };

  const removeOption = (idx: number) => {
    const opts = [...(field.options || [])];
    opts.splice(idx, 1);
    updateField({ options: opts });
  };

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>Field Properties</div>

      <label style={labelStyle}>
        Key
        <input
          value={field.key}
          onChange={(e) => !field.locked && updateField({ key: e.target.value })}
          disabled={field.locked}
          style={{ ...inputStyle, ...(field.locked ? disabledStyle : {}) }}
        />
      </label>

      <label style={labelStyle}>
        Label
        <input
          value={field.label}
          onChange={(e) => updateField({ label: e.target.value })}
          style={inputStyle}
        />
      </label>

      <label style={labelStyle}>
        Type
        <select
          value={field.type}
          onChange={(e) => updateField({ type: e.target.value as FormFieldType })}
          disabled={field.locked}
          style={{ ...inputStyle, ...(field.locked ? disabledStyle : {}) }}
        >
          {FIELD_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </label>

      <div style={checkRowStyle}>
        <label style={checkLabelStyle}>
          <input
            type="checkbox"
            checked={field.required}
            onChange={(e) => updateField({ required: e.target.checked })}
          />
          Required
        </label>
        <label style={checkLabelStyle}>
          <input type="checkbox" checked={field.locked} disabled />
          Locked
        </label>
      </div>

      <label style={labelStyle}>
        Placeholder
        <input
          value={field.placeholder ?? ''}
          onChange={(e) => updateField({ placeholder: e.target.value || undefined })}
          style={inputStyle}
        />
      </label>

      <label style={labelStyle}>
        Help Text
        <input
          value={field.helpText ?? ''}
          onChange={(e) => updateField({ helpText: e.target.value || undefined })}
          style={inputStyle}
        />
      </label>

      {(field.type === 'select' || field.type === 'radio') && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Options</div>
          {(field.options || []).map((opt, idx) => (
            <div key={idx} style={optionRowStyle}>
              <input
                value={opt.value}
                onChange={(e) => updateOption(idx, { value: e.target.value })}
                placeholder="value"
                style={{ ...optionInputStyle, flex: 1 }}
              />
              <input
                value={opt.label}
                onChange={(e) => updateOption(idx, { label: e.target.value })}
                placeholder="label"
                style={{ ...optionInputStyle, flex: 2 }}
              />
              <button onClick={() => removeOption(idx)} style={optionDeleteStyle}>&times;</button>
            </div>
          ))}
          <button onClick={addOption} style={addOptionBtnStyle}>+ Add option</button>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Visibility Rule</div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Show this field only when another field has a specific value</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <select
            value={field.visibleWhen?.field ?? ''}
            onChange={(e) => {
              if (!e.target.value) {
                updateField({ visibleWhen: undefined });
              } else {
                updateField({ visibleWhen: { field: e.target.value, equals: field.visibleWhen?.equals ?? '' } });
              }
            }}
            style={{ ...inputStyle, flex: 1 }}
          >
            <option value="">Always visible</option>
            {allFieldKeys.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
          {field.visibleWhen?.field && (
            <>
              <span style={{ fontSize: 11, color: '#6b7280' }}>=</span>
              <input
                value={typeof field.visibleWhen.equals === 'string' ? field.visibleWhen.equals : (field.visibleWhen.equals || []).join(',')}
                onChange={(e) =>
                  updateField({
                    visibleWhen: { field: field.visibleWhen!.field, equals: e.target.value },
                  })
                }
                placeholder="value"
                style={{ ...inputStyle, flex: 1 }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  padding: 16,
  overflow: 'auto',
  background: '#fff',
  borderLeft: '1px solid #e5e7eb',
};

const panelHeaderStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#111827',
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid #e5e7eb',
};

const emptyStyle: React.CSSProperties = {
  padding: 24,
  textAlign: 'center',
  color: '#9ca3af',
  fontSize: 13,
  borderLeft: '1px solid #e5e7eb',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: '#374151',
  marginBottom: 10,
};

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '6px 8px',
  marginTop: 4,
  fontSize: 12,
  border: '1px solid #d1d5db',
  borderRadius: 4,
  outline: 'none',
  boxSizing: 'border-box',
};

const disabledStyle: React.CSSProperties = {
  background: '#f3f4f6',
  color: '#9ca3af',
  cursor: 'not-allowed',
};

const checkRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 16,
  marginBottom: 10,
};

const checkLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: '#374151',
};

const optionRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  marginBottom: 4,
  alignItems: 'center',
};

const optionInputStyle: React.CSSProperties = {
  padding: '4px 6px',
  fontSize: 11,
  border: '1px solid #d1d5db',
  borderRadius: 3,
};

const optionDeleteStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#d1d5db',
  fontSize: 16,
  padding: '0 2px',
};

const addOptionBtnStyle: React.CSSProperties = {
  fontSize: 11,
  padding: '3px 8px',
  border: '1px dashed #d1d5db',
  borderRadius: 3,
  background: 'transparent',
  cursor: 'pointer',
  color: '#6b7280',
  marginTop: 4,
};
